import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import { BASE_API_URL, BASE_DOMAIN } from '../helpers/Config';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const ViewTeamScreen = ({ theme = 'light' }) => {
    const [selectedLevel, setSelectedLevel] = useState('all');
    const [teamData, setTeamData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ReferCode, setReferCode] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);

    const navigate = useNavigate();

    const isDarkMode = theme === 'dark';

    // THEME STYLES
    const pageStyles = {
        maxWidth: '480px',
        margin: '0 auto',
        minHeight: '100vh',
        backgroundColor: isDarkMode ? '#111827' : '#f8fafc',
        color: isDarkMode ? '#f9fafb' : '#111827',
        padding: '12px',
        transition: 'background-color 0.25s ease, color 0.25s ease'
    };

    const cardBase = {
        backgroundColor: isDarkMode ? '#020617' : '#ffffff',
        borderRadius: '14px',
        border: `1px solid ${isDarkMode ? '#1f2937' : '#e5e7eb'}`,
        boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.35)' : '0 4px 16px rgba(15, 23, 42, 0.06)',
        color: isDarkMode ? '#e5e7eb' : '#111827',
        transition: 'background-color 0.25s ease, border 0.25s ease, color 0.25s ease'
    };

    const mutedTextStyle = {
        color: isDarkMode ? '#9ca3af' : '#6c757d'
    };

    const inputBase = {
        backgroundColor: isDarkMode ? '#020617' : '#ffffff',
        color: isDarkMode ? '#e5e7eb' : '#111827',
        borderColor: isDarkMode ? '#334155' : '#dee2e6'
    };

    const inputGroupTextStyle = {
        backgroundColor: isDarkMode ? '#020617' : '#ffffff',
        color: isDarkMode ? '#9ca3af' : '#6c757d',
        borderColor: isDarkMode ? '#334155' : '#dee2e6'
    };

    // Copy referral link to clipboard
    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(`${BASE_DOMAIN}/register/${ReferCode}`);
            setCopySuccess(true);
            toast.success("Link copied successfully", {
                position: 'bottom-center'
            })
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = `${BASE_DOMAIN}/register/${ReferCode}`;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    const FetchProfile = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/account/profile`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
                return;
            }

            if (data?.data) {
                setReferCode(data?.data?.code);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        FetchProfile();
        fetchTeamData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch team data from API
    const fetchTeamData = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem('a5srf4wer7w1cd');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.post(`${BASE_API_URL}/team/view-team`, {
                token: token,
            });

            const data = response?.data;

            if (data && data.data && Array.isArray(data.data)) {
                // Transform API response to match our component structure
                const transformedData = data.data.map((member, index) => ({
                    id: member.user_id?.toString() || `temp-${index}`,
                    username: `User Id: ${member.user_id}`,
                    level: member.level,
                    totalDeposit: member.deposit || 0,
                    totalWithdrawal: member.withdrawl || 0, // Note: API uses 'withdrawl' (typo)
                    activeStaking: member.staking || 0,
                    user_id: member.user_id // Keep original user_id for reference
                }));

                setTeamData(transformedData);
            } else {
                setTeamData([]);
            }
        } catch (err) {
            console.error('Error fetching team data:', err);
            setError(err.response?.data?.message || 'Failed to fetch team data');
            setTeamData([]);
        } finally {
            setLoading(false);
        }
    };

    // Filter data based on selected level and search term
    const filteredData = teamData.filter(member => {
        const levelMatch = selectedLevel === 'all' || member.level === parseInt(selectedLevel);
        const searchMatch = member.username.toLowerCase().includes(searchTerm.toLowerCase());
        return levelMatch && searchMatch;
    });

    // Format number without currency symbol
    const formatNumber = (amount) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    // Calculate totals for current filter
    const calculateTotals = () => {
        return filteredData.reduce((totals, member) => ({
            totalDeposit: totals.totalDeposit + member.totalDeposit,
            totalWithdrawal: totals.totalWithdrawal + member.totalWithdrawal,
            totalStaking: totals.totalStaking + member.activeStaking,
            totalMembers: totals.totalMembers + 1
        }), { totalDeposit: 0, totalWithdrawal: 0, totalStaking: 0, totalMembers: 0 });
    };

    const totals = calculateTotals();

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.5
            }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.4
            }
        },
        hover: {
            scale: 1.02,
            transition: {
                duration: 0.2
            }
        }
    };

    // Refresh handler
    const handleRefresh = () => {
        fetchTeamData();
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="min-vh-100"
            style={pageStyles}
        >
            <div className="container-fluid p-3">
                {/* Header */}
                <motion.div
                    initial={{ y: -30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.7 }}
                    className="text-center mb-3 pt-3"
                >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h1 className="h3 fw-bold mb-0 text-primary">Team Management</h1>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleRefresh}
                            disabled={loading}
                            className={`btn btn-sm ${isDarkMode ? 'btn-outline-light' : 'btn-outline-primary'}`}
                        >
                            <i className={`fas fa-refresh ${loading ? 'fa-spin' : ''}`}></i>
                        </motion.button>
                    </div>
                    <p className="small" style={mutedTextStyle}>
                        View and manage your team members
                    </p>
                </motion.div>

                {/* Referral Link Section */}
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                    className="mb-4"
                >
                    <div className="card bg-gradient-primary text-white border-0 shadow-sm">
                        <div className="card-body p-3">
                            <div className="d-flex align-items-center mb-2">
                                <i className="fas fa-share-alt me-2"></i>
                                <h6 className="card-title mb-0 fw-bold">Share & Earn</h6>
                            </div>
                            <p className="small opacity-75 mb-2">
                                Share your referral link and earn rewards when friends join!
                            </p>
                            <div className="d-flex align-items-stretch">
                                <div className="flex-grow-1 me-2">
                                    <input
                                        type="text"
                                        readOnly
                                        value={`${BASE_DOMAIN}/register/${ReferCode}`}
                                        className="form-control form-control-sm"
                                        style={{
                                            fontSize: '0.75rem',
                                            backgroundColor: '#ffffff',
                                            color: '#111827',
                                            borderColor: 'transparent'
                                        }}
                                        onClick={(e) => e.target.select()}
                                    />
                                </div>
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={copyToClipboard}
                                    className={`btn btn-sm ${copySuccess ? 'btn-success' : 'btn-light'} shadow-sm`}
                                    style={{ minWidth: '70px' }}
                                >
                                    {copySuccess ? (
                                        <>
                                            <i className="fas fa-check me-1"></i>
                                            Copied!
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-copy me-1"></i>
                                            Copy
                                        </>
                                    )}
                                </motion.button>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Loading State */}
                {loading && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center py-5"
                    >
                        <div className={`spinner-border ${isDarkMode ? 'text-light' : 'text-primary'}`} role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2" style={mutedTextStyle}>
                            Loading team data...
                        </p>
                    </motion.div>
                )}

                {/* Error State */}
                {error && !loading && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="alert alert-danger text-center"
                    >
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {error}
                        <button
                            className={`btn btn-sm ms-2 ${isDarkMode ? 'btn-outline-light' : 'btn-outline-danger'}`}
                            onClick={handleRefresh}
                        >
                            Retry
                        </button>
                    </motion.div>
                )}

                {/* Content when not loading and no error */}
                {!loading && !error && (
                    <>
                        {/* Combined Summary Stats */}
                        <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                            className="row g-2 mb-3"
                        >
                            <div className="col-6">
                                <motion.div
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.02 }}
                                    className="card bg-primary text-white border-0 shadow-sm"
                                >
                                    <div className="card-body text-center p-2">
                                        <h6 className="card-title opacity-75 mb-1 small">Total Members</h6>
                                        <h5 className="fw-bold mb-0">{totals.totalMembers}</h5>
                                    </div>
                                </motion.div>
                            </div>
                            <div className="col-6">
                                <motion.div
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.02 }}
                                    className="card bg-success text-white border-0 shadow-sm"
                                >
                                    <div className="card-body text-center p-2">
                                        <h6 className="card-title opacity-75 mb-1 small">Total Deposit</h6>
                                        <h5 className="fw-bold mb-0">{formatNumber(totals.totalDeposit)}</h5>
                                    </div>
                                </motion.div>
                            </div>
                            <div className="col-6">
                                <motion.div
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.02 }}
                                    className="card bg-warning text-white border-0 shadow-sm"
                                >
                                    <div className="card-body text-center p-2">
                                        <h6 className="card-title opacity-75 mb-1 small">Total Withdrawal</h6>
                                        <h5 className="fw-bold mb-0">{formatNumber(totals.totalWithdrawal)}</h5>
                                    </div>
                                </motion.div>
                            </div>
                            <div className="col-6">
                                <motion.div
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.02 }}
                                    className="card bg-info text-white border-0 shadow-sm"
                                >
                                    <div className="card-body text-center p-2">
                                        <h6 className="card-title opacity-75 mb-1 small">Active Staking</h6>
                                        <h5 className="fw-bold mb-0">{formatNumber(totals.totalStaking)}</h5>
                                    </div>
                                </motion.div>
                            </div>
                        </motion.div>

                        {/* Controls Section */}
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="mb-4"
                        >
                            <div className="card shadow-sm border-0" style={cardBase}>
                                <div className="card-body p-3">
                                    <div className="row g-2">
                                        <div className="col-12">
                                            <label
                                                htmlFor="levelSelect"
                                                className="form-label fw-semibold small"
                                                style={{ color: isDarkMode ? '#e5e7eb' : '#111827' }}
                                            >
                                                Select Level
                                            </label>
                                            <select
                                                id="levelSelect"
                                                className="form-select form-select-sm"
                                                value={selectedLevel}
                                                onChange={(e) => setSelectedLevel(e.target.value)}
                                                style={inputBase}
                                            >
                                                <option value="all">All Levels</option>
                                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(level => (
                                                    <option key={level} value={level}>Level {level}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-12">
                                            <label
                                                htmlFor="searchInput"
                                                className="form-label fw-semibold small"
                                                style={{ color: isDarkMode ? '#e5e7eb' : '#111827' }}
                                            >
                                                Search Username
                                            </label>
                                            <div className="input-group input-group-sm">
                                                <span
                                                    className="input-group-text"
                                                    style={inputGroupTextStyle}
                                                >
                                                    <i className="fas fa-search" />
                                                </span>
                                                <input
                                                    type="text"
                                                    id="searchInput"
                                                    className="form-control"
                                                    placeholder="Search username..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    style={inputBase}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Team List */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="mb-4"
                        >
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6
                                    className="fw-bold mb-0"
                                    style={{ color: isDarkMode ? '#e5e7eb' : '#111827' }}
                                >
                                    Team Members
                                </h6>
                                <span className="badge bg-primary rounded-pill">
                                    {filteredData.length}
                                </span>
                            </div>

                            <AnimatePresence>
                                {filteredData.length > 0 ? (
                                    <motion.div
                                        initial="hidden"
                                        animate="visible"
                                        exit="hidden"
                                        className="team-space-y-3"
                                    >
                                        {filteredData.map((member, index) => (
                                            <motion.div
                                                key={member.id}
                                                variants={cardVariants}
                                                initial="hidden"
                                                animate="visible"
                                                whileHover="hover"
                                                transition={{ delay: index * 0.1 }}
                                                className="card shadow-sm border-0"
                                                style={cardBase}
                                            >
                                                <div className="card-body p-3">
                                                    {/* Username and Level */}
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <h6
                                                            className="fw-bold mb-0"
                                                            style={{ color: isDarkMode ? '#e5e7eb' : '#111827' }}
                                                        >
                                                            {member.username}
                                                        </h6>
                                                        <span className="badge bg-secondary rounded-pill px-3">
                                                            L{member.level}
                                                        </span>
                                                    </div>

                                                    {/* Financial Data */}
                                                    <div className="row g-2 text-center">
                                                        <div className="col-4">
                                                            <div
                                                                className="border rounded p-2"
                                                                style={{
                                                                    borderColor: isDarkMode ? '#1f2937' : '#dee2e6'
                                                                }}
                                                            >
                                                                <small
                                                                    className="d-block"
                                                                    style={mutedTextStyle}
                                                                >
                                                                    Deposit
                                                                </small>
                                                                <motion.span
                                                                    initial={{ opacity: 0 }}
                                                                    animate={{ opacity: 1 }}
                                                                    transition={{ delay: index * 0.1 + 0.2 }}
                                                                    className="fw-bold text-success"
                                                                >
                                                                    {formatNumber(member.totalDeposit)}
                                                                </motion.span>
                                                            </div>
                                                        </div>
                                                        <div className="col-4">
                                                            <div
                                                                className="border rounded p-2"
                                                                style={{
                                                                    borderColor: isDarkMode ? '#1f2937' : '#dee2e6'
                                                                }}
                                                            >
                                                                <small
                                                                    className="d-block"
                                                                    style={mutedTextStyle}
                                                                >
                                                                    Withdrawal
                                                                </small>
                                                                <motion.span
                                                                    initial={{ opacity: 0 }}
                                                                    animate={{ opacity: 1 }}
                                                                    transition={{ delay: index * 0.1 + 0.3 }}
                                                                    className="fw-bold text-warning"
                                                                >
                                                                    {formatNumber(member.totalWithdrawal)}
                                                                </motion.span>
                                                            </div>
                                                        </div>
                                                        <div className="col-4">
                                                            <div
                                                                className="border rounded p-2"
                                                                style={{
                                                                    borderColor: isDarkMode ? '#1f2937' : '#dee2e6'
                                                                }}
                                                            >
                                                                <small
                                                                    className="d-block"
                                                                    style={mutedTextStyle}
                                                                >
                                                                    Staking
                                                                </small>
                                                                <motion.span
                                                                    initial={{ opacity: 0 }}
                                                                    animate={{ opacity: 1 }}
                                                                    transition={{ delay: index * 0.1 + 0.4 }}
                                                                    className="fw-bold text-info"
                                                                >
                                                                    {formatNumber(member.activeStaking)}
                                                                </motion.span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="card border-0 text-center py-5"
                                        style={cardBase}
                                    >
                                        <div className="card-body">
                                            <i className="fas fa-users fa-2x mb-3" style={mutedTextStyle}></i>
                                            <h6 style={mutedTextStyle}>
                                                {teamData.length === 0
                                                    ? "No team members found"
                                                    : selectedLevel === 'all'
                                                        ? "No team members match your search"
                                                        : `No users found at Level ${selectedLevel}`
                                                }
                                            </h6>
                                            <p className="small" style={mutedTextStyle}>
                                                {teamData.length === 0
                                                    ? "You don't have any team members yet"
                                                    : "Try changing your filters or search term"
                                                }
                                            </p>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    </>
                )}
            </div>

            {/* Add Font Awesome for icons */}
            <link
                rel="stylesheet"
                href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css"
            />

            {/* Mobile-specific styles */}
            <style>{`
                .team-space-y-3 > * + * {
                    margin-top: 1rem;
                }
                .bg-gradient-primary {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
                }
            `}</style>
        </motion.div>
    );
};

export default ViewTeamScreen;
